import { Component, Input, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { NgChartsModule } from 'ng2-charts';
import { ChartData, ChartOptions } from 'chart.js';
import { MobileNavService } from '../../../shared/mobile/mobile-bottom-nav/mobile-nav.service';
import { MobilePageShellComponent } from '../../../shared/mobile/mobile-page-shell/mobile-page-shell.component';
import { MobileMetricRibbonComponent, MobileMetric } from '../../../shared/mobile/mobile-metric-ribbon/mobile-metric-ribbon.component';
import { MobileDataCardComponent, MobileCardField } from '../../../shared/mobile/mobile-data-card/mobile-data-card.component';
import { SingleSelectDropdownComponent } from '../../business-details/single-select-dropdown.component';
import {
  RawMaterial,
  ProductProject,
  FixedOverhead,
  VariableOverhead,
  DashboardSummary,
} from '../main-dashboard.component';

@Component({
  selector: 'app-mobile-main-dashboard-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgChartsModule,
    MobilePageShellComponent,
    MobileMetricRibbonComponent,
    MobileDataCardComponent,
    SingleSelectDropdownComponent,
  ],
  templateUrl: './mobile-main-dashboard-view.component.html',
  styleUrls: ['./mobile-main-dashboard-view.component.css'],
})
export class MobileMainDashboardViewComponent implements OnInit, OnDestroy, OnChanges {
  @Input() dashboardSummary!: DashboardSummary;
  @Input() rawMaterialsData: RawMaterial[] = [];
  @Input() productProjectsData: ProductProject[] = [];
  @Input() fixedOverheadsData: FixedOverhead[] = [];
  @Input() variableOverheadsData: VariableOverhead[] = [];
  @Input() uniqueRawMaterialUOMs: string[] = [];
  @Input() uniqueFixedCostPeriods: string[] = [];
  @Input() uniqueVariableCostUOMs: string[] = [];
  @Input() costAllocationData!: ChartData<'bar'>;
  @Input() costAllocationOptions!: ChartOptions<'bar'>;
  @Input() profitTrendData: any = {};
  @Input() profitTrendOptions!: ChartOptions<'bar'>;

  mobileView: 'overview' | 'tables' | 'charts' = 'overview';
  activeTab: 'raw' | 'products' | 'fixed' | 'variable' = 'raw';

  private _rawSearch = '';
  get rawSearch() { return this._rawSearch; }
  set rawSearch(v: string) { this._rawSearch = v; this.rawCurrentPage = 1; }

  private _productsSearch = '';
  get productsSearch() { return this._productsSearch; }
  set productsSearch(v: string) { this._productsSearch = v; this.productsCurrentPage = 1; }

  private _fixedSearch = '';
  get fixedSearch() { return this._fixedSearch; }
  set fixedSearch(v: string) { this._fixedSearch = v; this.fixedCurrentPage = 1; }

  private _variableSearch = '';
  get variableSearch() { return this._variableSearch; }
  set variableSearch(v: string) { this._variableSearch = v; this.variableCurrentPage = 1; }

  private _selectedRawUOM: string | null = null;
  get selectedRawUOM() { return this._selectedRawUOM; }
  set selectedRawUOM(v: string | null) { this._selectedRawUOM = v; this.rawCurrentPage = 1; }

  private _selectedFixedPeriod: string | null = null;
  get selectedFixedPeriod() { return this._selectedFixedPeriod; }
  set selectedFixedPeriod(v: string | null) { this._selectedFixedPeriod = v; this.fixedCurrentPage = 1; }

  private _selectedVariableUOM: string | null = null;
  get selectedVariableUOM() { return this._selectedVariableUOM; }
  set selectedVariableUOM(v: string | null) { this._selectedVariableUOM = v; this.variableCurrentPage = 1; }

  rawFiltersOpen = false;
  productsFiltersOpen = false;
  fixedFiltersOpen = false;
  variableFiltersOpen = false;

  readonly pageSize = 10;
  rawCurrentPage = 1;
  productsCurrentPage = 1;
  fixedCurrentPage = 1;
  variableCurrentPage = 1;

  readonly Math = Math;
  readonly String = String;

  private subs: Subscription[] = [];
  private currencyPipe = new CurrencyPipe('en-ZA');

  constructor(
    private mobileNavService: MobileNavService,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.subs.push(
      this.mobileNavService.activeTab.subscribe(tab => {
        this.activeTab = tab as any;
        this.rawCurrentPage = this.productsCurrentPage = this.fixedCurrentPage = this.variableCurrentPage = 1;
        this.updateBadges();
      }),
      this.route.queryParams.subscribe(params => {
        const view = params['view'];
        if (view === 'tables') {
          this.mobileView = 'tables';
        } else if (view === 'charts') {
          this.mobileView = 'charts';
        } else {
          this.mobileView = 'overview';
        }
      }),
    );
  }

  ngOnChanges(): void {
    this.updateBadges();
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private updateBadges(): void {
    this.mobileNavService.updateBadges({
      raw: this.filteredRaw.length,
      products: this.filteredProducts.length,
      fixed: this.filteredFixed.length,
      variable: this.filteredVariable.length,
    });
  }

  // ── Option helpers for SingleSelectDropdown ──────────────────────────────────
  toStringOption = (v: string) => ({ value: v, label: v });

  get rawUOMOptions() { return this.uniqueRawMaterialUOMs.map(this.toStringOption); }
  get fixedPeriodOptions() { return this.uniqueFixedCostPeriods.map(this.toStringOption); }
  get variableUOMOptions() { return this.uniqueVariableCostUOMs.map(this.toStringOption); }

  // ── Filtered getters ──────────────────────────────────────────────────────────
  get filteredRaw(): RawMaterial[] {
    const q = this.rawSearch.toLowerCase().trim();
    return this.rawMaterialsData.filter(m => {
      const matchSearch = !q || m.materialName.toLowerCase().includes(q) || m.uom.toLowerCase().includes(q);
      const matchUOM = !this.selectedRawUOM || m.uom === this.selectedRawUOM;
      return matchSearch && matchUOM;
    });
  }

  get filteredProducts(): ProductProject[] {
    const q = this.productsSearch.toLowerCase().trim();
    if (!q) return [...this.productProjectsData];
    return this.productProjectsData.filter(p => p.productName.toLowerCase().includes(q));
  }

  get filteredFixed(): FixedOverhead[] {
    const q = this.fixedSearch.toLowerCase().trim();
    return this.fixedOverheadsData.filter(o => {
      const matchSearch = !q || o.name.toLowerCase().includes(q) || o.paymentPeriod.toLowerCase().includes(q);
      const matchPeriod = !this.selectedFixedPeriod || o.paymentPeriod === this.selectedFixedPeriod;
      return matchSearch && matchPeriod;
    });
  }

  get filteredVariable(): VariableOverhead[] {
    const q = this.variableSearch.toLowerCase().trim();
    return this.variableOverheadsData.filter(o => {
      const matchSearch = !q || o.name.toLowerCase().includes(q) || o.uom.toLowerCase().includes(q);
      const matchUOM = !this.selectedVariableUOM || o.uom === this.selectedVariableUOM;
      return matchSearch && matchUOM;
    });
  }

  // ── Metric getters ────────────────────────────────────────────────────────────
  private fmt(val: number): string {
    return this.currencyPipe.transform(val, 'ZAR', 'symbol', '1.2-2') ?? '';
  }

  get overviewMetrics(): MobileMetric[] {
    if (!this.dashboardSummary) return [];
    const s = this.dashboardSummary;
    return [
      { label: 'Raw Materials', value: this.fmt(s.totalRawMaterialValue) },
      { label: 'Products', value: this.fmt(s.totalProductValue) },
      { label: 'Fixed OH', value: this.fmt(s.totalFixedOverheads) },
      { label: 'Variable OH', value: this.fmt(s.totalVariableOverheads) },
      { label: 'Orders', value: s.totalOrders },
      { label: 'Pending', value: s.totalPendingOrders, warning: s.totalPendingOrders > 0 },
    ];
  }

  get tabMetrics(): MobileMetric[] {
    if (!this.dashboardSummary) return [];
    const s = this.dashboardSummary;
    switch (this.activeTab) {
      case 'raw':
        return [
          { label: 'Items', value: this.filteredRaw.length },
          { label: 'Total Value', value: this.fmt(s.totalRawMaterialValue) },
        ];
      case 'products':
        return [
          { label: 'Items', value: this.filteredProducts.length },
          { label: 'Total Value', value: this.fmt(s.totalProductValue) },
        ];
      case 'fixed':
        return [
          { label: 'Items', value: this.filteredFixed.length },
          { label: 'Total', value: this.fmt(s.totalFixedOverheads) },
        ];
      case 'variable':
        return [
          { label: 'Items', value: this.filteredVariable.length },
          { label: 'Total', value: this.fmt(s.totalVariableOverheads) },
        ];
    }
  }

  // ── Card field mappers ────────────────────────────────────────────────────────
  rawFields(item: RawMaterial): MobileCardField[] {
    return [
      { label: 'Quantity', value: `${item.quantity.toFixed(2)} ${item.uom}`, chip: true },
      { label: 'Value', value: `R ${item.value.toFixed(2)}` },
    ];
  }

  productFields(item: ProductProject): MobileCardField[] {
    return [
      { label: 'Quantity', value: item.quantity.toFixed(2), chip: true },
      { label: 'Value', value: `R ${item.value.toFixed(2)}` },
    ];
  }

  fixedFields(item: FixedOverhead): MobileCardField[] {
    return [
      { label: 'Period', value: item.paymentPeriod, chip: true },
      { label: 'Amount', value: `R ${item.amount.toFixed(2)}` },
    ];
  }

  variableFields(item: VariableOverhead): MobileCardField[] {
    return [
      { label: 'Qty', value: `${item.totalQuantity.toFixed(2)} ${item.uom}`, chip: true },
      { label: 'Unit Cost', value: `R ${item.unitCost.toFixed(2)}` },
      { label: 'Total Cost', value: `R ${item.totalCost.toFixed(2)}` },
    ];
  }

  // ── Totals ────────────────────────────────────────────────────────────────────
  get rawTotal(): number { return this.filteredRaw.reduce((s, m) => s + m.value, 0); }
  get productsTotal(): number { return this.filteredProducts.reduce((s, p) => s + p.value, 0); }
  get fixedTotal(): number { return this.filteredFixed.reduce((s, o) => s + o.amount, 0); }
  get variableTotal(): number { return this.filteredVariable.reduce((s, o) => s + o.totalCost, 0); }

  // ── Cost allocation chart height ──────────────────────────────────────────────
  get costAllocChartHeight(): string {
    return `${Math.max(200, 80 + 4 * 44)}px`;
  }

  // ── Pagination ────────────────────────────────────────────────────────────────
  get filteredRawTotalPages(): number { return Math.max(1, Math.ceil(this.filteredRaw.length / this.pageSize)); }
  get filteredRawPaged(): RawMaterial[] { const s = (this.rawCurrentPage - 1) * this.pageSize; return this.filteredRaw.slice(s, s + this.pageSize); }
  changeRawPage(page: number): void { if (page >= 1 && page <= this.filteredRawTotalPages) this.rawCurrentPage = page; }

  get filteredProductsTotalPages(): number { return Math.max(1, Math.ceil(this.filteredProducts.length / this.pageSize)); }
  get filteredProductsPaged(): ProductProject[] { const s = (this.productsCurrentPage - 1) * this.pageSize; return this.filteredProducts.slice(s, s + this.pageSize); }
  changeProductsPage(page: number): void { if (page >= 1 && page <= this.filteredProductsTotalPages) this.productsCurrentPage = page; }

  get filteredFixedTotalPages(): number { return Math.max(1, Math.ceil(this.filteredFixed.length / this.pageSize)); }
  get filteredFixedPaged(): FixedOverhead[] { const s = (this.fixedCurrentPage - 1) * this.pageSize; return this.filteredFixed.slice(s, s + this.pageSize); }
  changeFixedPage(page: number): void { if (page >= 1 && page <= this.filteredFixedTotalPages) this.fixedCurrentPage = page; }

  get filteredVariableTotalPages(): number { return Math.max(1, Math.ceil(this.filteredVariable.length / this.pageSize)); }
  get filteredVariablePaged(): VariableOverhead[] { const s = (this.variableCurrentPage - 1) * this.pageSize; return this.filteredVariable.slice(s, s + this.pageSize); }
  changeVariablePage(page: number): void { if (page >= 1 && page <= this.filteredVariableTotalPages) this.variableCurrentPage = page; }
}
